import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Coins, TrendingDown, ArrowRight, Sparkles, Share2, Linkedin,
  MessageCircle, Copy, Check, Info, Calculator as CalcIcon, Instagram,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

// === Varianti di branding (stesso motore di calcolo) ===
const VARIANTS = {
  linkedin: {
    id: "linkedin",
    brandA: "Alassane", brandB: "Diallo",
    accent: "#D4AF37",
    bodyFont: "'Manrope', sans-serif",
    overline: "Report gratuito · Piano Azione · dati reali ISTAT",
    titleA: "Quanto perdi lasciando i risparmi ",
    titleAccent: "fermi sul conto?",
    subtitle: "Genera in 60 secondi il tuo Report Piano Azione. Basato sull'inflazione reale (ISTAT) e sul rendimento storico documentato dell'oro.",
    showGold: true, showChartGold: true, showValoreReale: true,
    ctaType: "form",
    ctaTitle: "Prenota una call conoscitiva",
    ctaText: "Lascia i tuoi dati: ti ricontatto per una call di 20 minuti, senza impegno.",
    submitLabel: "Prenota la call di 20 minuti",
    backHref: "/",
    footer: "Alassane Diallo · Protezione Patrimoniale · Dati: ISTAT, World Gold Council",
    showSources: true,
  },
  ig: {
    id: "ig",
    brandA: "Alex", brandB: "Gold",
    accent: "#C9A44B",
    bodyFont: "'Poppins', sans-serif",
    overline: "La verità sui tuoi risparmi",
    titleA: "I tuoi soldi fermi stanno ",
    titleAccent: "sparendo.",
    subtitle: "Calcola in 30 secondi quanto perdi. Senza che tu te ne accorga.",
    showGold: false, showChartGold: false, showValoreReale: true,
    ctaType: "dm",
    ctaTitle: "C'è un modo per fermare tutto questo.",
    ctaText: "Ma non lo trovi qui. Scrivimi \"MAPPA\" in DM e ti mostro come.",
    submitLabel: "Scrivi \"MAPPA\" in DM",
    igHandle: "alexgold.oro",
    dmUrl: "https://www.instagram.com/alexgold.oro/",
    backHref: null,
    footer: "Alex Gold",
    showSources: false,
  },
};

function detectVariant() {
  if (typeof window === "undefined") return "linkedin";
  const params = new URLSearchParams(window.location.search);
  const v = params.get("v");
  if (v === "ig" || v === "linkedin") return v;
  const host = window.location.hostname.toLowerCase();
  if (host.includes("alexgold")) return "ig";
  return "linkedin";
}

const SCENARIOS = [
  { key: "prudente", label: "Prudente", value: 1.5, note: "1,5%" },
  { key: "storica", label: "Media storica (10 anni)", value: 2.0, note: "2,0% · ISTAT" },
  { key: "elevato", label: "Scenario elevato (tipo 2022-23)", value: 5.7, note: "5,7%" },
];

export default function Calculator() {
  const [V] = useState(() => VARIANTS[detectVariant()]);
  const acc = V.accent;

  const [capitale, setCapitale] = useState(50000);
  const [anni, setAnni] = useState(5);
  const [inflazione, setInflazione] = useState(2.0);
  const [scenario, setScenario] = useState("storica");
  const [loading] = useState(false);
  const [result, setResult] = useState(null);
  const [gold, setGold] = useState(null);
  const [copied, setCopied] = useState(false);

  const [lead, setLead] = useState({ nome: "", email: "", telefono: "" });
  const [leadDone, setLeadDone] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [consenso, setConsenso] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/piano-azione` : "";

  useEffect(() => { document.title = `${V.brandA} ${V.brandB} — Piano Azione`; }, [V]);
  useEffect(() => {
    if (V.showGold) axios.get(`${API}/gold/spot`).then((r) => setGold(r.data)).catch(() => {});
  }, [V]);

  const Overline = ({ children }) => (
    <span className="block mb-3 uppercase font-semibold" style={{ fontSize: "0.72rem", letterSpacing: "0.22em", color: acc }}>{children}</span>
  );

  const pickScenario = (s) => { setScenario(s.key); setInflazione(s.value); };

  const inviaReport = async (e) => {
    e.preventDefault();
    if (!lead.nome || !lead.email || !lead.telefono) { toast.error("Inserisci nome, email e telefono."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(lead.email)) { toast.error("Inserisci un'email valida."); return; }
    if ((lead.telefono.match(/\d/g) || []).length < 7) { toast.error("Inserisci un numero di telefono valido."); return; }
    if (!consenso) { toast.error("Devi accettare l'informativa privacy per continuare."); return; }
    setLeadLoading(true);
    try {
      const riepilogo = result
        ? `Calcolo: ${eur(result.capitale)} fermi per ${result.anni} anni, inflazione ${result.inflazione}% -> perdita reale ${eur(result.perdita)} (${result.perdita_pct}%).`
        : "Richiesta report calcolatore.";
      await axios.post(`${API}/leads`, {
        nome: lead.nome, email: lead.email, telefono: lead.telefono, tipo: "cliente",
        messaggio: `[Piano Azione ${V.id}] ${riepilogo}`,
      });
      setLeadDone(true);
      toast.success("Richiesta inviata! Ti ricontatto a breve.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nell'invio. Riprova.");
    } finally { setLeadLoading(false); }
  };

  // Gate lead-first: salva i dati PRIMA di mostrare il report (variante form)
  const apriGate = () => {
    if (!capitale || capitale <= 0) { toast.error("Inserisci un capitale valido."); return; }
    setGateOpen(true);
    setTimeout(() => document.getElementById("gate-form")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const generaConGate = async (e) => {
    e.preventDefault();
    if (!capitale || capitale <= 0) { toast.error("Inserisci un capitale valido."); return; }
    if (!lead.nome || !lead.email || !lead.telefono) { toast.error("Inserisci nome, email e telefono."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(lead.email)) { toast.error("Inserisci un'email valida."); return; }
    if ((lead.telefono.match(/\d/g) || []).length < 7) { toast.error("Inserisci un numero di telefono valido."); return; }
    if (!consenso) { toast.error("Devi accettare l'informativa privacy per continuare."); return; }
    setLeadLoading(true);
    let data;
    try {
      const res = await axios.post(`${API}/calc`, {
        capitale: Number(capitale), anni: Number(anni), inflazione: Number(inflazione), variant: V.id,
      });
      data = res.data;
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore nel calcolo. Riprova.");
      setLeadLoading(false);
      return;
    }

    // Il calcolo è riuscito: mostra SEMPRE il report
    setResult(data);
    setTimeout(() => document.getElementById("risultato")?.scrollIntoView({ behavior: "smooth" }), 150);

    // Invio dati separato: se fallisce, l'utente vede comunque il report
    try {
      const riepilogo = `Calcolo: ${eur(data.capitale)} fermi per ${data.anni} anni, inflazione ${data.inflazione}% -> perdita reale ${eur(data.perdita)} (${data.perdita_pct}%).`;
      await axios.post(`${API}/leads`, {
        nome: lead.nome, email: lead.email, telefono: lead.telefono, tipo: "cliente",
        messaggio: `[Piano Azione ${V.id}] ${riepilogo}`,
      });
      setLeadDone(true);
      toast.success("Report generato! Ti ricontatto a breve.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Report generato, ma non è stato possibile salvare i tuoi dati. Riprova.");
    } finally {
      setLeadLoading(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Link copiato!"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copia non riuscita."); }
  };

  const waText = encodeURIComponent(`Ho scoperto quanto perdo lasciando i risparmi fermi sul conto 👇\n${shareUrl}`);
  const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3.5 text-zinc-100 placeholder-zinc-500 focus:outline-none transition-all duration-300";
  const focusStyle = (e) => { e.target.style.borderColor = acc; };
  const blurStyle = (e) => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#FAFAFA]" style={{ fontFamily: V.bodyFont }}>
      {/* Header */}
      <header className="border-b border-white/5 glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href={V.backHref || "#"} className="flex items-center gap-2.5" data-testid="calc-logo">
            <Coins style={{ color: acc }} strokeWidth={1.5} size={24} />
            <span className="font-serif text-xl font-semibold text-white">{V.brandA} <span style={{ color: acc }}>{V.brandB}</span></span>
          </a>
          {V.backHref && <a href={V.backHref} className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="calc-back">← Torna al sito</a>}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-14">
        {/* Intro */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto">
          <Overline>{V.overline}</Overline>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-white" data-testid="calc-title">
            {V.titleA}<span style={{ color: acc }}>{V.titleAccent}</span>
          </h1>
          <p className="mt-5 text-zinc-400">{V.subtitle}</p>
          {V.showGold && gold?.available && (
            <div className="mt-5 inline-flex items-center gap-2 text-sm text-zinc-400 glass border border-white/10 rounded-full px-4 py-2" data-testid="gold-spot">
              <Coins size={15} style={{ color: acc }} />
              Oro oggi: <span className="font-semibold" style={{ color: acc }}>{eur(gold.price_eur_gram)}/g</span>
              <span className="text-zinc-600">· live</span>
            </div>
          )}
        </motion.div>

        {/* Form */}
        <div className="mt-12 glass rounded-3xl border border-white/10 p-8 md:p-10 max-w-3xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Capitale fermo sul conto</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
                <input type="number" min="0" className={inputCls + " pl-8"} value={capitale}
                  onFocus={focusStyle} onBlur={blurStyle}
                  onChange={(e) => setCapitale(e.target.value)} placeholder="50000" data-testid="input-capitale" />
              </div>
              {capitale && Number(capitale) > 0 && Number(capitale) < 20000 ? (
                <p className="text-xs text-amber-400/80 mt-2" data-testid="capitale-hint">
                  Con importi sotto i 20.000€ il beneficio della protezione si apprezza soprattutto nel lungo periodo.
                </p>
              ) : (
                <p className="text-xs text-zinc-600 mt-2">Importo consigliato: da 50.000€ in su.</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Per quanti anni? <span style={{ color: acc }} className="font-semibold">{anni}</span></label>
              <input type="range" min="1" max="30" value={anni} onChange={(e) => setAnni(e.target.value)}
                style={{ accentColor: acc }} className="w-full mt-4" data-testid="input-anni" />
              <div className="flex justify-between text-xs text-zinc-600 mt-1"><span>1 anno</span><span>30 anni</span></div>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm text-zinc-400 mb-2">Scenario di inflazione</label>
            <div className="grid sm:grid-cols-3 gap-3">
              {SCENARIOS.map((s) => {
                const active = scenario === s.key;
                return (
                  <button key={s.key} type="button" onClick={() => pickScenario(s)}
                    className="px-3 py-3 rounded-xl border text-sm transition-all duration-300 text-left"
                    style={active ? { borderColor: acc, background: `${acc}1A`, color: acc } : { borderColor: "rgba(255,255,255,0.1)", color: "#a1a1aa" }}
                    data-testid={`scenario-${s.key}`}>
                    <div className="font-medium">{s.label}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{s.note}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm text-zinc-500">Personalizza:</span>
              <input type="range" min="0" max="10" step="0.1" value={inflazione}
                onChange={(e) => { setInflazione(e.target.value); setScenario("custom"); }}
                style={{ accentColor: acc }} className="flex-1" data-testid="input-inflazione" />
              <span className="font-semibold w-14 text-right" style={{ color: acc }}>{Number(inflazione).toFixed(1)}%</span>
            </div>
          </div>

          {!gateOpen ? (
            <button onClick={apriGate} disabled={loading}
              className="btn-acc mt-8 w-full px-8 py-4 rounded-full text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: acc }} data-testid="calc-button">
              <CalcIcon size={18} /> {V.id === "ig" ? "Scopri quanto perdi" : "Genera il mio Report gratis"}
            </button>
          ) : (
            <form id="gate-form" onSubmit={generaConGate} className="mt-8 pt-6 border-t border-white/10 space-y-3" data-testid="gate-form">
              <div className="text-center mb-1">
                <div className="font-serif text-xl text-white">{V.id === "ig" ? "Sblocca il tuo risultato" : "Ricevi il tuo Report gratis"}</div>
                <p className="text-sm text-zinc-400 mt-1">
                  {V.id === "ig"
                    ? "Inserisci i tuoi dati per vedere subito quanto stai perdendo."
                    : "Inserisci i tuoi dati: ti mostro subito il report personalizzato e ti ricontatto per approfondire."}
                </p>
              </div>
              <input className={inputCls} placeholder="Nome e cognome" value={lead.nome} onFocus={focusStyle} onBlur={blurStyle}
                onChange={(e) => setLead({ ...lead, nome: e.target.value })} data-testid="gate-nome" />
              <input type="email" className={inputCls} placeholder="La tua email" value={lead.email} onFocus={focusStyle} onBlur={blurStyle}
                onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="gate-email" />
              <input className={inputCls} placeholder="Telefono" value={lead.telefono} onFocus={focusStyle} onBlur={blurStyle}
                onChange={(e) => setLead({ ...lead, telefono: e.target.value })} data-testid="gate-telefono" />
              <label className="flex items-start gap-2.5 text-xs text-zinc-400 leading-relaxed cursor-pointer select-none" data-testid="gate-consenso-label">
                <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0" style={{ accentColor: acc }} data-testid="gate-consenso" />
                <span>
                  Ho letto e accetto l'
                  <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:opacity-80" style={{ color: acc }} data-testid="gate-privacy-link">informativa sulla privacy</a>
                  {" "}e acconsento al trattamento dei miei dati per essere ricontattato.
                </span>
              </label>
              <button type="submit" disabled={leadLoading}
                className="btn-acc w-full px-8 py-4 rounded-full text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: acc }} data-testid="gate-submit">
                {leadLoading ? "Elaborazione in corso..." : <><CalcIcon size={18} /> {V.id === "ig" ? "Mostrami quanto perdo" : "Mostrami il Report"}</>}
              </button>
            </form>
          )}
        </div>

        {/* Risultato */}
        {result && (
          <motion.div id="risultato" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mt-14 max-w-4xl mx-auto scroll-mt-28" data-testid="calc-result">
            <div className={`grid gap-5 ${V.showGold ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              <div className="glass rounded-2xl border border-red-500/20 p-7">
                <TrendingDown className="text-red-400 mb-3" strokeWidth={1.5} size={26} />
                <div className="text-sm text-zinc-400">Perdita di potere d'acquisto</div>
                <div className="font-serif text-4xl text-red-400 mt-1" data-testid="result-perdita">-{eur(result.perdita)}</div>
                <div className="text-sm text-zinc-500 mt-1">{result.perdita_pct}% in {result.anni} anni</div>
              </div>
              <div className="glass rounded-2xl border border-white/10 p-7">
                <div className="text-sm text-zinc-400">Valore reale tra {result.anni} anni</div>
                <div className="font-serif text-3xl text-white mt-1">{eur(result.valore_reale)}</div>
                <div className="text-xs text-zinc-500 mt-1">di {eur(result.capitale)} nominali</div>
              </div>
              {V.showGold && (
                <div className="glass rounded-2xl border border-white/10 p-7">
                  <div className="text-sm text-zinc-400">Scenario oro (storico {result.oro_storico.cagr_pct}%)*</div>
                  <div className="font-serif text-3xl mt-1" style={{ color: acc }}>{eur(result.oro_storico.valore_nominale)}</div>
                  <div className="text-xs text-zinc-500 mt-1">+{eur(result.oro_storico.guadagno)} potenziali</div>
                </div>
              )}
            </div>

            {/* Grafico */}
            <div className="glass rounded-2xl border border-white/10 p-6 md:p-8 mt-5">
              <Overline>{V.showChartGold ? `Conto corrente vs oro (prudente ${result.oro_prudente.cagr_pct}%)` : "Il tuo capitale, anno dopo anno"}</Overline>
              <div className="h-72 mt-4">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <LineChart data={result.serie} margin={{ left: 10, right: 10, top: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="anno" stroke="#71717A" tickFormatter={(v) => `${v}a`} />
                    <YAxis stroke="#71717A" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={40} />
                    <Tooltip formatter={(v) => eur(v)} labelFormatter={(l) => `Anno ${l}`}
                      contentStyle={{ background: "#121214", border: `1px solid ${acc}55`, borderRadius: 12, color: "#fff" }} />
                    <Legend />
                    <Line type="monotone" dataKey="conto" name="Fermo sul conto" stroke="#f87171" strokeWidth={2.5} dot={false} />
                    {V.showChartGold && <Line type="monotone" dataKey="oro_prudente" name="Oro (prudente)" stroke={acc} strokeWidth={2.5} dot={false} />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Commento AI */}
            <div className="glass rounded-2xl border p-7 mt-5" style={{ borderColor: `${acc}33` }}>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={18} style={{ color: acc }} />
                <span className="uppercase font-semibold" style={{ fontSize: "0.72rem", letterSpacing: "0.22em", color: acc }}>
                  {V.id === "ig" ? "Il dato che non ti aspetti" : "Analisi personalizzata"}
                </span>
              </div>
              <p className="text-zinc-200 leading-relaxed font-serif text-lg" data-testid="result-commento">{result.commento}</p>
            </div>

            {/* Fonti / disclaimer (solo LinkedIn) */}
            {V.showSources && (
              <div className="flex items-start gap-2 mt-4 text-xs text-zinc-600">
                <Info size={14} className="mt-0.5 shrink-0" />
                <p>*Rendimenti passati non garantiscono risultati futuri; l'oro è un asset volatile. Dati: inflazione ISTAT (indice NIC),
                  rendimento storico oro in EUR (media 20 anni ~10,7%, lungo periodo ~8%). Il calcolo ha scopo informativo.</p>
              </div>
            )}

            {/* CTA finale */}
            <div className="mt-8">
              {V.ctaType === "form" ? (
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="glass rounded-2xl border p-7" style={{ borderColor: `${acc}33` }}>
                    <h3 className="font-serif text-2xl text-white">{V.ctaTitle}</h3>
                    <p className="text-sm text-zinc-400 mt-1">{V.ctaText}</p>
                    {leadDone ? (
                      <div className="mt-5 flex items-center gap-3" style={{ color: acc }} data-testid="lead-success">
                        <Check size={20} /> Richiesta inviata! Ti ricontatto a breve.
                      </div>
                    ) : (
                      <form onSubmit={inviaReport} className="mt-5 space-y-3" data-testid="report-form">
                        <input className={inputCls} placeholder="Nome e cognome" value={lead.nome} onFocus={focusStyle} onBlur={blurStyle}
                          onChange={(e) => setLead({ ...lead, nome: e.target.value })} data-testid="report-nome" />
                        <input type="email" className={inputCls} placeholder="La tua email" value={lead.email} onFocus={focusStyle} onBlur={blurStyle}
                          onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="report-email" />
                        <input className={inputCls} placeholder="Telefono" value={lead.telefono} onFocus={focusStyle} onBlur={blurStyle}
                          onChange={(e) => setLead({ ...lead, telefono: e.target.value })} data-testid="report-telefono" />
                        <label className="flex items-start gap-2.5 text-xs text-zinc-400 leading-relaxed cursor-pointer select-none" data-testid="report-consenso-label">
                          <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 accent-current" style={{ accentColor: acc }} data-testid="report-consenso" />
                          <span>
                            Ho letto e accetto l'
                            <a href="/privacy" target="_blank" rel="noreferrer" className="underline hover:opacity-80" style={{ color: acc }} data-testid="report-privacy-link">informativa sulla privacy</a>
                            {" "}e acconsento al trattamento dei miei dati per essere ricontattato.
                          </span>
                        </label>
                        <button type="submit" disabled={leadLoading}
                          className="btn-acc w-full px-6 py-3.5 rounded-full text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                          style={{ backgroundColor: acc }} data-testid="report-submit">
                          {leadLoading ? "Invio..." : <>{V.submitLabel} <ArrowRight size={16} /></>}
                        </button>
                      </form>
                    )}
                  </div>
                  <ShareBox acc={acc} waText={waText} shareUrl={shareUrl} copied={copied} copyLink={copyLink} />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="glass rounded-2xl border p-8 flex flex-col justify-center" style={{ borderColor: `${acc}33` }} data-testid="dm-cta">
                    <h3 className="font-serif text-3xl text-white">{V.ctaTitle}</h3>
                    <p className="text-zinc-400 mt-2">{V.ctaText}</p>
                    <a href={V.dmUrl} target="_blank" rel="noreferrer"
                      className="btn-acc mt-6 px-6 py-4 rounded-full text-black font-semibold inline-flex items-center justify-center gap-2"
                      style={{ backgroundColor: acc }} data-testid="dm-button">
                      <Instagram size={18} /> {V.submitLabel}
                    </a>
                  </div>
                  <ShareBox acc={acc} waText={waText} shareUrl={shareUrl} copied={copied} copyLink={copyLink} />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-zinc-600">{V.footer}</footer>
    </div>
  );
}

const ShareBox = ({ acc, waText, shareUrl, copied, copyLink }) => (
  <div className="glass rounded-2xl border border-white/10 p-7">
    <div className="flex items-center gap-2 mb-4">
      <Share2 size={18} style={{ color: acc }} />
      <h3 className="font-serif text-2xl text-white">Condividi</h3>
    </div>
    <p className="text-sm text-zinc-400 mb-4">Fai scoprire ai tuoi contatti quanto perdono col denaro fermo.</p>
    <div className="grid grid-cols-1 gap-3">
      <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer"
        className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 hover:border-white/30 transition-all" data-testid="share-whatsapp">
        <MessageCircle size={18} className="text-[#25D366]" /> Condividi su WhatsApp
      </a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
        className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 hover:border-white/30 transition-all" data-testid="share-linkedin">
        <Linkedin size={18} className="text-[#0A66C2]" /> Condividi su LinkedIn
      </a>
      <button onClick={copyLink}
        className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 hover:border-white/30 transition-all text-left" data-testid="share-copy">
        {copied ? <Check size={18} style={{ color: acc }} /> : <Copy size={18} style={{ color: acc }} />}
        {copied ? "Link copiato!" : "Copia link (per Instagram bio/storie)"}
      </button>
    </div>
  </div>
);
